import React, { useState, useRef, useEffect, useContext } from 'react';
import {
    FaBars, FaHome, FaRobot, FaUsers, FaCog, FaChartBar, FaLock, FaComments, FaBell, FaInfoCircle, FaCode, FaNewspaper, FaTools, FaLink, FaShareAlt,
    FaYoutube, FaInstagram, FaTiktok, FaTwitter, FaImages, FaVideo
} from 'react-icons/fa';
import { AuthContext } from '../AuthContext';
import { db } from '../firebase';
import { collection, onSnapshot, doc, query, where, orderBy, setDoc, getDoc } from 'firebase/firestore';
import { PERMISSIONS, hasPermission } from '../utils/rbac';
import './NavigationBar.css';
import BaseGallery from './galleries/BaseGallery';  // Add if needed for rendering

const NavigationBar = ({ isPro, activeComponent, setActiveComponent, userRoles }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { isAdmin, user } = useContext(AuthContext);
    const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
    const [showMenuHint, setShowMenuHint] = useState(false);

    // Check if user is new and should see the menu hint
    useEffect(() => {
        if (!user?.uid) return;
        
        const checkFirstTimeUser = async () => {
            try {
                const userDocRef = doc(db, 'users', user.uid);
                const userDoc = await getDoc(userDocRef);
                
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    // If menuHintSeen doesn't exist or is false, show the hint
                    if (userData.menuHintSeen === undefined || userData.menuHintSeen === false) {
                        setShowMenuHint(true);
                        
                        // Update user document to mark hint as seen
                        setDoc(userDocRef, { menuHintSeen: true }, { merge: true });
                    }
                }
            } catch (error) {
                console.error("Error checking first-time user status:", error);
            }
        };
        
        checkFirstTimeUser();
    }, [user]);

    // Hide menu hint when menu is opened
    useEffect(() => {
        if (isMenuOpen && showMenuHint) {
            setShowMenuHint(false);
        }
    }, [isMenuOpen]);

    // Add this effect to check for unread messages
    useEffect(() => {
        if (!isPro || !user?.uid) return;
        
        // Listen for changes to the user's chat document
        const chatDocRef = doc(db, 'pro_chats', user.uid);
        const unsubscribe = onSnapshot(chatDocRef, (docSnapshot) => {
            if (docSnapshot.exists()) {
                const chatData = docSnapshot.data();
                // If there are unread messages from admin
                setHasUnreadMessages(chatData.unreadByUser > 0);
            }
        });
        
        return () => unsubscribe();
    }, [isPro, user]);
    
    // Add this effect to clear notification when Pro Chat is selected
    useEffect(() => {
        if (activeComponent === 'pro_chat' && hasUnreadMessages && user?.uid) {
            // Reset unread count when user navigates to pro_chat
            setDoc(doc(db, 'pro_chats', user.uid), {
                unreadByUser: 0
            }, { merge: true });
            
            // Update local state
            setHasUnreadMessages(false);
        }
    }, [activeComponent, hasUnreadMessages, user]);

    const handleNavClick = (view) => {
        const validViews = [
            'gallery', 'poem',
            'youtube', 'instagram', 'tiktok', 'twitter',
            'admin', 'pro_chat', 'pro_galleries', 'tech', 'whats_new', 'custom_build',
            'my_linktree', 'videos'
        ];
        
        if (validViews.includes(view)) {
            setActiveComponent(view);
            setIsMenuOpen(false);
        } else {
            console.warn('Attempted to navigate to unknown view:', view);
        }
    };

    const isActive = (component) => activeComponent === component;

    // Helper function to render nav item if user has permission
    const renderNavItem = (component, label, permission = null, icon = null) => {
        if (permission && !hasPermission(userRoles, permission)) {
            return null;
        }

        return (
            <button
                className={`nav-item ${isActive(component) ? 'active' : ''}`}
                onClick={() => handleNavClick(component)}
            >
                {icon && <span className="nav-icon">{icon}</span>}
                {label}
            </button>
        );
    };

    return (
        <nav className="navigation-bar">
            <div className="hamburger-container">
                <button className="hamburger" onClick={() => setIsMenuOpen(!isMenuOpen)}>
                    <FaBars />
                </button>
                
                {showMenuHint && (
                    <div className="menu-hint-arrow">
                        <div className="arrow-text">Launch Nav!</div>
                    </div>
                )}
            </div>

            <div className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
                {renderNavItem('whats_new', "What's New", null, <FaNewspaper />)}
                {renderNavItem('gallery', 'Gallery', null, <FaImages />)}
                {renderNavItem('pro_galleries', 'Pro Galleries', PERMISSIONS.VIEW_PRO_CONTENT, <FaImages />)}
                {renderNavItem('videos', 'Videos', null, <FaVideo />)}
                {renderNavItem('poem', 'Pro Poem', PERMISSIONS.USE_PRO_POEM, <FaRobot />)}
                {renderNavItem('pro_chat', 'Pro Chat', PERMISSIONS.USE_PRO_CHAT, <FaComments />)}
                {hasPermission(userRoles, PERMISSIONS.USE_CUSTOM_BUILD) && renderNavItem('custom_build', 'Custom Build', null, <FaTools />)}
                {renderNavItem('my_linktree', 'My LinkTree', null, <FaLink />)}
                {renderNavItem('tech', 'Tech', null, <FaCode />)}
                {renderNavItem('admin', 'Admin Dashboard', PERMISSIONS.VIEW_ADMIN_DASHBOARD, <FaCog />)}
            </div>
        </nav>
    );
};

export default NavigationBar;
